"use client";

import { trackConversion } from "@analytics";
import { isCalendlyEventScheduled } from "@marketing/demo/lib/calendly";
import { GTM_CONVERSION_EVENTS } from "@repo/analytics";
import { Button } from "@ui/components/button";
import { PlayCircleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

interface CalendlyBookingProps {
	calendlyUrl: string;
	className?: string;
}

export function CalendlyBooking({
	calendlyUrl,
	className,
}: CalendlyBookingProps) {
	const t = useTranslations();

	useEffect(() => {
		// Load Calendly script
		const script = document.createElement("script");
		script.src = "https://assets.calendly.com/assets/external/widget.js";
		script.async = true;
		document.head.appendChild(script);

		return () => {
			// Clean up script when component unmounts
			const existingScript = document.querySelector(
				'script[src="https://assets.calendly.com/assets/external/widget.js"]',
			);
			if (existingScript) {
				document.head.removeChild(existingScript);
			}
		};
	}, []);

	useEffect(() => {
		// The inline widget posts `calendly.event_scheduled` when a meeting is booked
		const handleMessage = (event: MessageEvent) => {
			if (isCalendlyEventScheduled(event)) {
				trackConversion(GTM_CONVERSION_EVENTS.GTM_DEMO_BOOKED, {
					source: "demo_page",
				});
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, []);

	return (
		<div className={className}>
			<div className="max-w-4xl mx-auto">
				<div className="text-center mb-12">
					<h2 className="text-3xl font-bold mb-4">
						{t("marketing.demo.booking.title")}
					</h2>
					<p className="text-xl text-muted-foreground max-w-2xl mx-auto">
						{t("marketing.demo.booking.description")}
					</p>
				</div>

				{/* Calendly Widget Container */}
				<div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
					<div
						className="calendly-inline-widget"
						data-url={calendlyUrl}
						style={{ minWidth: "320px", height: "700px" }}
					/>
				</div>

				{/* Alternative CTA */}
				<div className="text-center mt-8">
					<p className="text-sm text-muted-foreground mb-4">
						{t("marketing.demo.booking.alternative")}
					</p>
					<Button variant="outline" asChild>
						<a href="#recorded-demo">
							<PlayCircleIcon className="mr-2 size-4" />
							{t("marketing.demo.booking.watchRecorded")}
						</a>
					</Button>
				</div>
			</div>
		</div>
	);
}
